import './requestForm.scss';

import { openSplitViewState } from '@approvalmax/ui/src/components';
import { errorHelpers } from '@approvalmax/utils';
import { useSetAtom } from 'jotai';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { FC, memo } from 'react';
import bemFactory from 'react-bem-factory';
import { useMount } from 'react-use';
import { useUserBetaFeatures } from 'shared/hooks';

import { requestSelector } from '../selectors/pageSelectors';
import { getFieldsApprovalPermissionBySystemPurpose } from '../selectors/requestSelectors';
import {
    NetSuiteBillPaymentRequestForm,
    NetSuiteBillRequestForm,
    NetSuiteExpenseReportRequestForm,
    NetSuiteInvoiceRequestForm,
    NetSuiteJournalEntryRequestForm,
    NetSuitePurchaseOrderRequestForm,
    NetSuiteVendorRequestForm,
} from './netsuite';
import { QBooksBillForm } from './qbooks/QBooksBillForm/QBooksBillForm';
import QBooksExpenseForm from './qbooks/QBooksExpenseForm/QBooksExpenseForm';
import QBooksJournalEntryRequestForm from './qbooks/QBooksJournalEntryRequestForm/QBooksJournalEntryRequestForm';
import QBooksPurchaseOrderForm from './qbooks/QBooksPurchaseOrderForm/QBooksPurchaseOrderForm';
import { QBooksSalesInvoiceForm } from './qbooks/QBooksSalesInvoiceForm/QBooksSalesInvoiceForm';
import QBOVendorRequest from './qbooks/QBOVendorRequest/QBOVendorRequest';
import { StandaloneRequestForm } from './standalone/StandaloneRequestForm/StandaloneRequestForm';
import StandaloneRequestFormOld from './standaloneOld/StandaloneRequestForm';
import XeroAirwallexBatchPaymentForm from './xero/XeroAirwallexBatchPaymentForm/XeroAirwallexBatchPaymentForm';
import XeroAmaxPayBatchPaymentForm from './xero/XeroAmaxPayBatchPaymentForm/XeroAmaxPayBatchPaymentForm';
import XeroBillBatchPaymentForm from './xero/XeroBillBatchPaymentForm/XeroBillBatchPaymentForm';
import { XeroBillForm } from './xero/XeroBillForm';
import { XeroContactRequestForm } from './xero/XeroContactRequestForm/XeroContactRequestForm';
import { XeroCreditNotesPayableForm } from './xero/XeroCreditNotesPayableForm/XeroCreditNotesPayableForm';
import { XeroCreditNotesReceivableForm } from './xero/XeroCreditNotesReceivableForm/XeroCreditNotesReceivableForm';
import XeroManualJournalRequestForm from './xero/XeroManualJournalRequestForm/XeroManualJournalRequestForm';
import { XeroPurchaseOrderForm } from './xero/XeroPurchaseOrderForm/XeroPurchaseOrderForm';
import XeroQuoteForm from './xero/XeroQuoteForm/XeroQuoteForm';
import XeroSalesInvoiceForm from './xero/XeroSalesInvoiceForm/XeroSalesInvoiceForm';

const bem = bemFactory.block('reqf-request-form');

export interface RequestFormProps {
    className?: string;
}

const RequestForm: FC<RequestFormProps> = (props) => {
    const { className } = props;

    const request = useSelector(requestSelector);
    const setOpenSplitView = useSetAtom(openSplitViewState);

    const integrationCode = request.integrationCode;

    const [attachmentFieldPermission] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [domain.FieldSystemPurpose.XeroAttachment])
    );

    useMount(() => {
        if (
            integrationCode === domain.IntegrationCode.XeroBill &&
            (request.attachments.length > 0 || !attachmentFieldPermission.disabled)
        ) {
            setOpenSplitView(true);
        }
    });

    const { isBelgradeUIStandaloneForm } = useUserBetaFeatures();

    switch (integrationCode) {
        case domain.IntegrationCode.XeroPo:
            return <XeroPurchaseOrderForm request={request} className={bem.add(className)()} />;

        case domain.IntegrationCode.XeroQuote:
            return <XeroQuoteForm request={request} />;

        case domain.IntegrationCode.XeroBill:
            return <XeroBillForm request={request} className={bem.add(className)()} />;

        case domain.IntegrationCode.XeroBillBatchPayment:
            return <XeroBillBatchPaymentForm request={request} />;

        case domain.IntegrationCode.XeroAirwallexBatchPayment:
            return <XeroAirwallexBatchPaymentForm request={request} />;

        case domain.IntegrationCode.XeroAmaxPayBatchPayment:
            return <XeroAmaxPayBatchPaymentForm request={request} />;

        case domain.IntegrationCode.XeroContact:
            return <XeroContactRequestForm request={request} />;

        case domain.IntegrationCode.XeroManualJournal:
            return <XeroManualJournalRequestForm request={request} />;

        case domain.IntegrationCode.XeroInvoice:
            return <XeroSalesInvoiceForm request={request} />;

        case domain.IntegrationCode.XeroCreditNotesPayable:
            return <XeroCreditNotesPayableForm request={request} />;

        case domain.IntegrationCode.XeroCreditNotesReceivable:
            return <XeroCreditNotesReceivableForm request={request} />;

        case domain.IntegrationCode.QBooksPo:
            return <QBooksPurchaseOrderForm request={request} className={bem.add(className)()} />;

        case domain.IntegrationCode.QBooksInvoice:
            return <QBooksSalesInvoiceForm request={request} />;

        case domain.IntegrationCode.QBooksBill:
            return <QBooksBillForm className={bem.add(className)()} />;

        case domain.IntegrationCode.QBooksExpense:
            return <QBooksExpenseForm request={request} />;

        case domain.IntegrationCode.QBooksJournalEntry:
            return <QBooksJournalEntryRequestForm request={request} />;

        case domain.IntegrationCode.QBooksVendor:
            return <QBOVendorRequest request={request} />;

        case domain.IntegrationCode.NetSuiteBill:
            return <NetSuiteBillRequestForm request={request} />;

        case domain.IntegrationCode.NetSuitePO:
            return <NetSuitePurchaseOrderRequestForm request={request} />;

        case domain.IntegrationCode.NetSuiteExpenseReport:
            return <NetSuiteExpenseReportRequestForm request={request} />;

        case domain.IntegrationCode.NetSuiteBillPayment:
            return <NetSuiteBillPaymentRequestForm request={request} />;

        case domain.IntegrationCode.NetSuiteInvoice:
            return <NetSuiteInvoiceRequestForm request={request} />;

        case domain.IntegrationCode.NetSuiteJournalEntry:
            return <NetSuiteJournalEntryRequestForm request={request} />;

        case domain.IntegrationCode.NetSuiteVendor:
            return <NetSuiteVendorRequestForm request={request} />;

        case domain.IntegrationCode.DearPo:
            return null;

        case null:
            return isBelgradeUIStandaloneForm ? (
                <StandaloneRequestForm request={request} />
            ) : (
                <StandaloneRequestFormOld request={request} className={bem.add(className)()} />
            );

        case domain.IntegrationCode.NetSuiteSalesOrder:
        case domain.IntegrationCode.NetSuiteVRA:
        case domain.IntegrationCode.NetSuiteRA:
            throw errorHelpers.notSupportedError();

        default:
            throw errorHelpers.assertNever(integrationCode);
    }
};

export default memo(RequestForm);
